import { Location } from "@angular/common"
import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core"
import { AngularFirestore } from "@angular/fire/firestore"
import { ActivatedRoute, Router } from "@angular/router"
import { RouteInfo } from "@tr/models"
import { AuthService, DataService } from "@tr/services"
import { captainLinks, captainProfileLinks } from "app/captain-portal/captain-portal-links"
import { clientLinks, clientProfileLinks } from "app/client-portal/client-portal-links"
import { superLinks, superProfileLinks } from "app/super-portal/super-portal-links"
import { testerLinks, testerProfileLinks } from "app/tester-portal/tester-portal-links"
import { Subscription } from "rxjs"

declare var $: any

@Component({
  selector: "tr-logged-in-layout",
  templateUrl: "./logged-in-layout.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class LoggedInLayoutComponent implements OnInit, OnDestroy {
  public opened = true
  public links: RouteInfo[] = []
  public profileLinks: RouteInfo[] = []

  public photoUrl = ""
  public firstName = ""
  public lastName = ""
  public company = ""
  public project = ""
  public email = ""
  public uid = ""
  public lastRunId = ""
  public portal: "client" | "tester" | "captain" | "super" | "" = ""
  public totalUnresolved = 0
  public managerAlerts = 0

  private subscriptions: Subscription[] = []
  private listTitles: any[] = [
    ...clientLinks,
    ...clientProfileLinks,
    ...testerLinks,
    ...testerProfileLinks,
    ...captainLinks,
    ...captainProfileLinks,
    ...superLinks,
    ...superProfileLinks,
  ]

  constructor(
    private router: Router,
    private authService: AuthService,
    private location: Location,
    private dataService: DataService,
    private route: ActivatedRoute,
    private afs: AngularFirestore,
  ) {
    this.route.data.subscribe(data => {
      this.links = data.links || []
      this.profileLinks = data.profileLinks || []
      this.profileLinks = this.profileLinks.filter(profileMenuItems =>
        this.hasPermission(profileMenuItems.permissionsNeeded, this.authService.roles),
      )
      this.portal = data.portal

      if (this.portal === "client") {
        this.lastRunId = ""
        this.loadRunId()
      }

      if (this.portal === "tester") {
        this.loadUnresolvedFeedback()
      }

      if (this.portal === "captain") {
        this.loadUnresolvedFeedback()
        this.loadAlerts()
      }
    })
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.dataService.company.subscribe(company => this.company = company),
      this.dataService.uid.subscribe(uid => this.uid = uid),
      this.dataService.project.subscribe(project => this.project = project),
      this.dataService.email.subscribe(email => this.email = email),
      this.dataService.firstName.subscribe(firstName => this.firstName = firstName),
      this.dataService.lastName.subscribe(lastName => this.lastName = lastName),
      this.dataService.photoUrl.subscribe(photoUrl => this.photoUrl = photoUrl),
    )
  }

  public async loadRunId(): Promise<void> {
    const projectSubscription = this.dataService.projectData.subscribe(project => {
      if (project && project.project) {
        const lastRunId = project.lastRunId || ""
        if (this.lastRunId !== lastRunId) {
          this.lastRunId = lastRunId
          this.afs.collection("companies")
            .doc(this.dataService.company.value)
            .collection("projects")
            .doc<any>(project.project)
            .collection("Test Runs")
            .doc(this.lastRunId)
            .ref.get()
            .then(doc => {
              if (doc.exists) {
                this.lastRunId = doc.data().runId
              }
            })
        }
      }
    })

    this.subscriptions.push(projectSubscription)
  }

  public async loadAlerts(): Promise<void> {
    const alertsRef = await this.afs
      .collection("alerts")
      .ref.get()

    const alertDocRefs = await Promise.all(alertsRef.docs.map(async alert => {
      return this.afs
        .collection("alerts")
        .doc(alert.data().name)
        .collection("alerts")
        .ref.get()
    }))

    for (const alertDocRef of alertDocRefs) {
      const managerTableRow = []
      alertDocRef.docs.forEach(alertDoc => {
        const alertDocData = alertDoc.data()
        const reporters = alertDocData.reporters
        reporters.forEach(reporter => {
          if (reporter.platform) {
            if (alertDocData.platforms === reporter.platform) {
              if (
                reporter.managerStatus &&
                reporter.managerStatus.length > 0
              ) {
                if (
                  !alertDocData.managerReviewed &&
                  reporter.manager === this.uid
                ) {
                  managerTableRow.push(["alerts"])
                }
              } else {
                if (!alertDocData.resolved) {
                  managerTableRow.push(["tester alerts"])
                }
              }
            }
          }
        })
      })

      this.managerAlerts = managerTableRow.length
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
  }

  // TODO: Add permissions
  public hasPermission(permissionsNeeded: string[], roles: string[]): boolean {
    return !!roles.find(role => (permissionsNeeded ? permissionsNeeded.includes(role) : true))
  }

  // Auto open panel when its the current path
  public isNotCurrentPath(route: RouteInfo): boolean {
    return route.children
      ? !!((route.children || []).find(child => child.path === this.router.url))
      : route.path === this.router.url
  }

  public logout(): void {
    this.authService.logout()
  }

  public goToAccount(): void {
    this.router.navigate(['account'])
  }

  public getTitle(): string {
    let navigateTo = this.location.prepareExternalUrl(this.location.path())
    if (navigateTo.charAt(0) === "#") {
      navigateTo = navigateTo.slice(2)
    }
    for (const title of this.listTitles) {
      const parent = title
      if (parent.path === navigateTo) {
        return parent.title
      } else if (parent.children) {
        // tslint:disable-next-line:prefer-const
        let childrenFromUrl = navigateTo.split("/")[2]
        if (!childrenFromUrl || childrenFromUrl === undefined) {
          childrenFromUrl = navigateTo
        }
        for (const current of parent.children) {
          if (current.path === childrenFromUrl) {
            return current.title
          }
        }
      }
    }
    return this.convertPathToTitle(navigateTo)
  }

  private convertPathToTitle(navigateTo: string): string {
    const viewTitle = navigateTo.split("/")[1].split(";" || "?")[0].replace(/([A-Z])/g, " $1")
    return viewTitle.charAt(0).toUpperCase() + viewTitle.slice(1)
  }

  public isMobileMenu(): boolean {
    if ($(window).width() < 991) {
      return false
    }
    return true
  }

  public getPath(): string {
    return this.location.prepareExternalUrl(this.location.path())
  }

  public hasManagerRole(): boolean {
    return this.hasSeniorManagerRole()
      || this.dataService.roles.value.includes("Manager")
      || this.dataService.roles.value.includes("PartnerManager")
  }

  public hasSeniorManagerRole(): boolean {
    return this.dataService.roles.value.includes("SuperUser")
      || this.dataService.roles.value.includes("SeniorManager")
      || this.dataService.roles.value.includes("PartnerSeniorManager")
  }

  public async loadUnresolvedFeedback(): Promise<void> {
    const sevaTestersRef = await this.afs
      .collection("Seva Testers")
      .ref.get()
    for (const doc of sevaTestersRef.docs) {
      const feedbackRef = await this.afs
        .collection("Seva Testers")
        .doc(doc.id)
        .collection("Feedback")
        .ref.get()
      const unresolvedRows = []
      for (const doc1 of feedbackRef.docs) {
        if (!doc1.data().isResolved) {
          unresolvedRows.push(["alert"])
        }
      }
      this.totalUnresolved = unresolvedRows.length
    }
  }
}
